import type { WidgetSize, WorkspaceWidgetPlacement } from "@wms/domain";

/**
 * ТЗ «Рабочие столы» 31.10.2026 (UXW-21…25, CW-11…15): the layout of a desktop as plain data — the order of the widgets
 * and their sizes, never pixels. Two columns on a wide screen (a «full» widget spans both), one column on a narrow one
 * in the same logical order; the moves «Выше / Ниже / Влево / Вправо» are pure functions over that order.
 */
export type Placement = WorkspaceWidgetPlacement;
export type Direction = "up" | "down" | "left" | "right";

export interface Cell {
  code: string;
  row: number;
  col: number;
  span: 1 | 2;
}

/**
 * Where every widget stands — the same sparse auto-placement the CSS grid of the desktop does: a half takes the next
 * free column, a full one starts a new row and fills it. With one column every widget is a row of its own.
 */
export function positions(widgets: Placement[], columns: number): Cell[] {
  const cells: Cell[] = [];
  let row = 0;
  let col = 0;
  for (const w of widgets) {
    if (columns < 2) {
      cells.push({ code: w.code, row, col: 0, span: 1 });
      row += 1;
      continue;
    }
    if (w.size === "full") {
      if (col !== 0) {
        row += 1;
        col = 0;
      }
      cells.push({ code: w.code, row, col: 0, span: 2 });
      row += 1;
      continue;
    }
    cells.push({ code: w.code, row, col, span: 1 });
    col += 1;
    if (col === 2) {
      row += 1;
      col = 0;
    }
  }
  return cells;
}

/**
 * The widget a move exchanges places with, or null when there is nowhere to go. «Влево / Вправо» — the previous / next
 * position of the order; «Выше / Ниже» — the widget of the row above / below in the same column (a full row is one
 * place; a row without that column gives its last widget).
 */
export function moveTarget(widgets: Placement[], index: number, direction: Direction, columns: number): number | null {
  if (index < 0 || index >= widgets.length) return null;
  if (direction === "left") return index > 0 ? index - 1 : null;
  if (direction === "right") return index < widgets.length - 1 ? index + 1 : null;
  const cells = positions(widgets, columns);
  const me = cells[index];
  const row = me.row + (direction === "up" ? -1 : 1);
  const candidates = cells.map((c, i) => ({ c, i })).filter(({ c }) => c.row === row);
  if (candidates.length === 0) return null;
  if (me.span === 2) return candidates[0].i;
  const same = candidates.find(({ c }) => c.span === 2 || c.col === me.col);
  return (same ?? candidates[candidates.length - 1]).i;
}

export function canMove(widgets: Placement[], index: number, direction: Direction, columns: number): boolean {
  return moveTarget(widgets, index, direction, columns) !== null;
}

/** One step of a move: the widget and its target exchange places; nothing else moves. */
export function move(widgets: Placement[], index: number, direction: Direction, columns: number): Placement[] {
  const target = moveTarget(widgets, index, direction, columns);
  if (target === null) return widgets;
  const next = [...widgets];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** A drop: the widget leaves its place and takes position `to` (the others close up behind it). */
export function moveTo(widgets: Placement[], from: number, to: number): Placement[] {
  if (from === to || from < 0 || from >= widgets.length || to < 0 || to >= widgets.length) return widgets;
  const next = [...widgets];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** CW-13: one block of a kind — adding a widget that is already there changes nothing. */
export function addWidget(widgets: Placement[], code: string, size: WidgetSize): Placement[] {
  if (widgets.some((w) => w.code === code)) return widgets;
  return [...widgets, { code, size }];
}

/** UXW-24: «Убрать» takes the widget off this desktop only; it stays in the catalogue. */
export function removeWidget(widgets: Placement[], code: string): Placement[] {
  return widgets.filter((w) => w.code !== code);
}

export function setSize(widgets: Placement[], code: string, size: WidgetSize): Placement[] {
  return widgets.map((w) => (w.code === code ? { ...w, size } : w));
}

/**
 * CW-18: the saved desktop split into what is drawn now (the widgets of the catalogue of this principal, once each)
 * and what is kept for the day a taken right comes back (hidden or unknown codes) — never drawn, saved back unchanged.
 */
export function splitHidden(saved: Placement[], hidden: string[], catalog: string[]): { visible: Placement[]; kept: Placement[] } {
  const visible: Placement[] = [];
  const kept: Placement[] = [];
  for (const w of saved) {
    if (catalog.includes(w.code) && !hidden.includes(w.code)) {
      if (!visible.some((v) => v.code === w.code)) visible.push(w);
    } else if (!kept.some((k) => k.code === w.code)) {
      kept.push(w);
    }
  }
  return { visible, kept };
}

/** What a save sends: the drawn widgets in their order, then the kept ones unchanged at the end of the list. */
export function forSave(visible: Placement[], kept: Placement[]): Placement[] {
  return [...visible, ...kept.filter((k) => !visible.some((v) => v.code === k.code))];
}

/** «Стандартный вид»: the standard set of this cabinet, only the widgets this principal may place. */
export function standardLayout(standard: Placement[], catalog: string[]): Placement[] {
  return splitHidden(standard, [], catalog).visible;
}

export function sameLayout(a: Placement[], b: Placement[]): boolean {
  return a.length === b.length && a.every((w, i) => w.code === b[i].code && w.size === b[i].size);
}

/** The words a screen reader says after a move (UXW-22). */
export function positionText(label: string, index: number, total: number): string {
  return `Виджет «${label}» теперь на позиции ${index + 1} из ${total}`;
}
